import { Button, RelativeTime, Skeleton } from "@optymyse/suite-ui";
import type { HistoryChange, HistoryItem } from "../../../shared/types/directory";
import { useHistory } from "../../lib/directory";
import { addressLines } from "../../lib/format";
import { fill, strings } from "../../lib/strings";
import { ApiError } from "../../lib/api";
import { DirectoryProblem, Panel } from "./Page";

/** Each field's name as a change reads it. */
const FIELD_NAMES: Readonly<Record<HistoryChange["field"], string>> = {
  display_name: "the name",
  legal_name: "the legal name",
  domain: "the domain",
  website: "the website",
  notes: "the notes",
  given_name: "the given name",
  family_name: "the family name",
  preferred_name: "the preferred name",
  address: "the address",
  methods: "the ways to reach them",
  companies: "the customers they're linked to",
  external_ids: "the ids other systems know them by",
};

/** Each action as a sentence's verb, for a company and a contact alike. */
function whatOf(item: HistoryItem, record: string): string {
  const action = item.action.replace(/^(company|contact)\./, "");
  switch (action) {
    case "created":
      return `created the ${record}`;
    case "updated":
      return item.changes.length > 0
        ? `changed ${listOf(item.changes.map((change) => FIELD_NAMES[change.field]))}`
        : `saved the ${record}`;
    case "archived":
      return `archived the ${record}`;
    case "erasure_requested":
      return `asked for the ${record}'s erasure`;
    case "erasure_cancelled":
      return `undid the ${record}'s erasure`;
    case "erased":
      return `erased the ${record}`;
    case "merged":
      return `merged a duplicate into the ${record}`;
    case "merged_into":
      return `merged the ${record} into another`;
    case "merge_undone":
      return "undid a merge";
    case "company_merged":
      return "moved their link to a customer that was merged";
    case "company_merge_undone":
      return "moved their link back, as a merge was undone";
    default:
      return `changed the ${record} (${item.action})`;
  }
}

/** "a, b and c". */
function listOf(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words.at(-1)!}`;
}

function whoOf(item: HistoryItem): string {
  if (item.actor.type === "system") return strings.directory.history.system;
  return item.actor.name ?? strings.directory.history.someone;
}

/** A field's value before or after, in words. */
function valueOf(change: HistoryChange, side: "before" | "after"): string | null {
  if (!("before" in change)) return null;
  if (change.field === "address") {
    const lines = addressLines(change[side]);
    return lines.length > 0 ? lines.join(", ") : null;
  }
  return change[side];
}

/** What changed, field by field, where a field holds text or an address: from what it was to what it is. */
function Changes({ changes }: { changes: readonly HistoryChange[] }) {
  const shown = changes.filter((change) => "before" in change);
  if (shown.length === 0) return null;
  return (
    <dl className="mt-1.5 flex flex-col gap-1 text-secondary">
      {shown.map((change) => {
        const before = valueOf(change, "before");
        const after = valueOf(change, "after");
        return (
          <div key={change.field} className="min-w-0">
            <dt className="sr-only">{FIELD_NAMES[change.field]}</dt>
            <dd className="text-ink-muted">
              <span className="font-medium text-ink">{FIELD_NAMES[change.field].replace(/^the /, "")}</span>
              {": "}
              {change.field === "notes" ? (
                after === null ? (
                  "cleared"
                ) : (
                  <span className="whitespace-pre-wrap text-ink">{after}</span>
                )
              ) : (
                <>
                  {before === null ? (
                    <em>none</em>
                  ) : (
                    <span className="line-through decoration-ink-muted">{before}</span>
                  )}
                  {" → "}
                  {after === null ? <em>none</em> : <span className="text-ink">{after}</span>}
                </>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/**
 * What happened to a record, newest first (SuiteUi's README, "Lists": what happened starts newest first), a page at a
 * time as Link pages it: who made each change, in which app, what it changed, and when.
 */
export function History({ kind, id }: { kind: "company" | "contact"; id: string }) {
  const history = useHistory(kind, id);
  const record = kind === "company" ? "customer" : "contact";
  const items = history.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <Panel title={strings.directory.history.title}>
      {history.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : history.isError && items.length === 0 ? (
        <DirectoryProblem
          {...(history.error instanceof ApiError ? { message: history.error.message } : {})}
          onRetry={() => void history.refetch()}
        />
      ) : items.length === 0 ? (
        <p className="text-ink-muted">{strings.directory.history.empty}</p>
      ) : (
        <ol className="flex flex-col divide-y divide-line">
          {items.map((item, index) => (
            <li key={`${item.at}-${index}`} className="py-3 first:pt-0 last:pb-0">
              <p>
                <span className="font-medium">{whoOf(item)}</span> {whatOf(item, record)}{" "}
                <span className="text-ink-muted">
                  {fill(strings.directory.history.via, { app: item.via })} · <RelativeTime at={item.at} />
                </span>
              </p>
              <Changes changes={item.changes} />
            </li>
          ))}
        </ol>
      )}
      {history.hasNextPage && (
        <div className="mt-3">
          <Button
            size="sm"
            variant="secondary"
            pending={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            {strings.directory.history.older}
          </Button>
        </div>
      )}
    </Panel>
  );
}
